import omnivore from '@mapbox/leaflet-omnivore'
import * as turf from '@turf/turf'
import { errors } from '../../services/ruutsApi/errors'
import { KMLObject, Placemark } from '../../types/kml'
import { dataLayersAreas, dataLayersPoints } from './dataLayers'
import { GeoJSON } from '../GeoJSON/index'
import { messages } from '../messages'

const namingMap = {
  Perímetro: 'perimeter',
  Lotes: 'paddocks',
  Estratos: 'estratas',
  'Áreas de exclusión': 'exclusion',
  'Sitios de monitoreo': 'monitoringSites',
  'Otros sitios': 'otherSites',
  'Otras áreas': 'otherPolygons',
  Unassigned: 'unassigned',
  'Unassigned Points': 'unassignedPoints',
} as const

export type FolderName = (typeof namingMap)[keyof typeof namingMap]
export type Folder = Record<FolderName | string, { features: GeoJSON.Feature[] }>
export type FolderArray = { name: FolderName; features: GeoJSON.Feature[] | GeoJSON.Point[] }[]

function objectToXML(obj: unknown) {
  // Start with the KML document structure
  let xml =
    '<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n'

  const convertValueToXML = (value: unknown, propName: string) => {
    if (Array.isArray(value)) {
      return value
        .map(arrayItem => `<${propName}>\n${convertValueToXML(arrayItem, '')}</${propName}>\n`)
        .join('')
    }
    if (typeof value === 'object' && value !== null) {
      let nestedXml = ''
      Object.keys(value).forEach(key => {
        nestedXml += `<${key}>`
        nestedXml += convertValueToXML(value[key], key)
        nestedXml += `</${key}>\n`
      })
      return nestedXml
    }
    return value.toString()
  }

  Object.keys(obj).forEach(prop => {
    xml += `<${prop}>`
    xml += convertValueToXML(obj[prop], prop)
    xml += `</${prop}>\n`
  })

  // Close the KML document structure
  xml += '</Document>\n</kml>'

  return xml
}

const handlePoint = (point: { coordinates: string }, name: string) => {
  const partialCoord = point.coordinates.split(',')
  const coordinates = [
    parseFloat(partialCoord[0]),
    parseFloat(partialCoord[1]),
    parseFloat(partialCoord[2]),
  ]

  const newPoint = turf.point(coordinates, {
    name,
    featureGroup: 'unassignedPoints',
  })

  return newPoint
}

function featureFromPlacemark(placemark: Placemark, category: string, name: string) {
  const placemarkXML = objectToXML({ Placemark: placemark })
  const features = omnivore.kml.parse(placemarkXML).toGeoJSON() as GeoJSON.FeatureCollection
  if (features.features.length !== 1) {
    throw new errors.BusinessEntityError({
      needSupport: false,
      message: messages.getMessage(
        messages.keys
          .ERROR_IMPORTING_FARM_FROM_KML_UNSUPPORTED_MULTI_GEOMETRY_DUPLICATES_NAMES_GEOMETRY_MESSAGE,
        [category, name],
      ),
    })
  }
  const feature = features.features[0]
  feature.properties.featureGroup = namingMap[category as FolderName]

  return feature
}

const placemarkToFeatures = (placemark: Placemark | Placemark[], category: string) => {
  const newFeatures: GeoJSON.Feature[] = []
  if (!placemark) return newFeatures

  if (!Array.isArray(placemark)) {
    let newFeature: GeoJSON.Feature
    if (placemark?.Point) {
      newFeature = handlePoint(placemark.Point, placemark.name)
    } else {
      newFeature = featureFromPlacemark(placemark, category, placemark.name)
    }
    newFeatures.push(newFeature)
  } else {
    placemark.forEach(p => {
      const newFeature = placemarkToFeatures(p, category)
      newFeatures.push(...newFeature)
    })
  }

  return newFeatures
}

export const plantillaParsing = (plantilla: KMLObject): FolderArray | null => {
  if (!(plantilla?.kml?.Document?.Folder && plantilla?.kml?.Document?.Folder?.length === 7)) {
    return null
  }

  const folders: Folder = {}
  let i = 0

  dataLayersAreas.forEach(layer => {
    folders[layer.farmLayerType] = {
      features: [],
    }
  })

  dataLayersPoints.forEach(layer => {
    folders[layer.farmLayerType] = {
      features: [],
    }
  })

  let featuresToAssign = []
  plantilla?.kml?.Document?.Folder?.forEach(folder => {
    const features = placemarkToFeatures(folder.Placemark, folder.name)

    const featuresByPlacemark = features.map(feature => {
      let returnFeature = { ...feature }
      const { type } = feature.geometry

      switch (type) {
        case 'Point':
          returnFeature.properties.featureGroup = 'unassignedPoints'
          returnFeature.properties.id = i
          break
        case 'Polygon':
          returnFeature.properties.area = GeoJSON.readableArea(GeoJSON.area(returnFeature))
          returnFeature.properties.id = i
          break
        case 'MultiPolygon':
          returnFeature.properties.area = GeoJSON.readableArea(GeoJSON.area(returnFeature))
          returnFeature.properties.id = i
          break
        case 'GeometryCollection': {
          const { geometries } = returnFeature.geometry as GeoJSON.GeometryCollection
          if (!geometries || !geometries.length) {
            returnFeature.properties.area = 'No area'
            returnFeature.properties.id = i
            break
          }

          try {
            // Filter and validate geometries to ensure they are Polygons or MultiPolygons
            const validGeometries = geometries.filter(
              (geom): geom is GeoJSON.Polygon | GeoJSON.MultiPolygon =>
                geom?.type === 'Polygon' || geom?.type === 'MultiPolygon',
            )

            if (validGeometries.length === 0) {
              console.warn('No valid Polygon or MultiPolygon geometries found')
              returnFeature.properties.area = 'No valid geometries'
              returnFeature.properties.id = i
              return returnFeature
            }

            const turfUnion = validGeometries.reduce(
              (
                a: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>,
                b: GeoJSON.Polygon | GeoJSON.MultiPolygon,
              ) => {
                if (!a || !a.type || !b || !b.type) {
                  return a || turf.feature(b)
                }
                return turf.union(a, turf.feature(b))
              },
              turf.feature(validGeometries[0]),
            )

            if (turfUnion) {
              turfUnion.properties = {
                ...returnFeature.properties,
                name: returnFeature.properties.name,
                area: GeoJSON.readableArea(GeoJSON.area(returnFeature)),
                id: i,
              }
              returnFeature = turfUnion
            } else {
              returnFeature.properties.area = 'No area'
              returnFeature.properties.id = i
            }
          } catch (error) {
            console.error('Error during geometry union:', error)
            returnFeature.properties.area = 'Error calculating area'
            returnFeature.properties.id = i
          }

          break
        }
        default:
          return null
      }
      i++
      return returnFeature
    })

    featuresToAssign = [...featuresToAssign, ...featuresByPlacemark]
  })

  featuresToAssign.forEach(fta => folders[fta.properties.featureGroup].features.push(fta))

  const foldersArray: FolderArray = Object.keys(folders).map(folderName => {
    const obj = {
      name: folderName as FolderName,
      features: folders[folderName].features,
    }
    return obj
  })
  return foldersArray
}
